/**
 * Generates an array of epoch timestamps (in seconds) spaced by a given gap (in minutes)
 * between a start time and an end time.
 *
 * @param {number} startTime - Start time in epoch seconds.
 * @param {number} endTime - End time in epoch seconds.
 * @param {number} gapInMinutes - Gap between each timestamp in minutes.
 * @returns {number[]} An array of epoch timestamps (in seconds) spaced by the given interval.
 *
 * Example:
 * generateEpochIntervals(1762618957, 1762705357, 10)
 * ➜ [1762618957, 1762624957, 1762630957, ...]
 */
export function generateEpochIntervals(
    startTime: number,
    endTime: number,
    gapInMinutes: number
): number[] {
    const result: number[] = [];
    const gapInSeconds = gapInMinutes * 60;
    if (!Number.isFinite(startTime) || !Number.isFinite(endTime) || !Number.isFinite(gapInSeconds) || gapInSeconds <= 0 || endTime < startTime) {
        return result;
    }

    for (let t = startTime; t <= endTime; t += gapInSeconds) {
        result.push(t);
    }
    return result;
}

/**
 * Generates an array of time intervals (in minutes) with a fixed gap.
 *
 * @param {number} duration - Total duration in minutes.
 * @param {number} [gap=10] - Gap between each interval in minutes (default is 10).
 * @returns {number[]} An array of minute marks from 0 up to the given duration, spaced by the gap.
 *
 * Example:
 * generateTimeIntervals(60)
 * ➜ [0, 10, 20, 30, 40, 50, 60]
 */
export function generateTimeIntervals(duration: number, gap = 10) {
    const result: number[] = [];
    if (!Number.isFinite(duration) || !Number.isFinite(gap) || duration < 0 || gap <= 0) {
        return result;
    }
    for (let i = 0; i <= duration; i += gap) {
        result.push(i);
    }
    return result;
}

/**
 * Calculates the size of each frame based on the total duration and the number of frames.
 * 
 * @param {number} timeFrame - Time Frame in min.
 * @param {number} windowWidth - Width of the frame window in pixels.
 * @returns {object} Object containing frameSize, frameOffset, frameInterval, pixelInSec
 */
export const calculateFrameSize = (
    timeFrame: number,
    windowWidth: number,
): { frameSize: number, frameOffset: number, frameInterval: number, pixelInSec: number } => {
    const Intervals: Record<number, number> = {
        30: 5, // 30 min
        60: 10, // 1 hour
        120: 20, // 2 hours
        180: 30, // 3 hours
        360: 60, // 6 hours
        720: 120, // 12 hours
    }
    const Offsets: Record<number, number> = {
        30: 1, // 30 min
        60: 5, // 1 hour
        120: 10, // 2 hours
        180: 15, // 3 hours
        360: 30, // 6 hours
        720: 60, // 12 hours
    }

    const safeTimeFrame = Offsets[timeFrame] ? timeFrame : 30;
    const safeWindowWidth = Math.max(windowWidth, 1);
    const frameOffset = Offsets[safeTimeFrame];
    const frameInterval = Intervals[safeTimeFrame];
    const frameSize = (safeWindowWidth / safeTimeFrame) * frameOffset;

    return {
        frameSize: frameSize,
        frameOffset: frameOffset,
        frameInterval: frameInterval,
        pixelInSec: frameSize / (frameOffset * 60)
    };
}

/**
 * Controls timeline playback speed & state.
 *
 * Accepts a control type and the current speed/value,
 * then returns the updated timeline value based on the action.
 *
 * controlType:
 *  - 'next'     → speed to higher step (1 → 2 → 8 → 32)
 *  - 'previous' → speed in reverse steps (-1 → -2 → -8 → -32)
 *  - 'play'     → start playback, return 'pause'
 *  - 'pause'    → stop playback, return 'play'
 *
 * @param {string} controlType - The control action type
 * @param {number} value - Current speed or direction
 * @returns {number | string} Updated numeric speed or toggled state ('play' / 'pause')
 *
 * @example
 * onTimelineControl('next', 2) → 8
 * onTimelineControl('pause', 0) → "play"
 */
export const onTimelineControl = (controlType: 'next' | 'previous' | 'play' | 'pause', value: number): number | string => {
    switch (controlType) {
        case 'next':
            if (value === 32) return 1
            switch (value) {
                case 1:
                    return 2
                case 2:
                    return 8
                case 8:
                    return 32
                default:
                    return 2
            }
        case 'previous':
            if (value === -32) return 1
            switch (value) {
                case 1:
                    return -2
                case -2:
                    return -8
                case -8:
                    return -32
                default:
                    return -2
            }
        case 'play':
            return 'pause'
        case 'pause':
            return 'play'
        default:
            return 'play'
    }
}


/**
 * Helper: Given a current timeframe (in minutes) and direction, return next timeframe step.
 * Steps supported: 30min, 60min, 120min, 180min, 360min, 720min
 */
export const getNextTimeFrame = (current: number, direction: 'up' | 'down') => {
    const steps = [30, 60, 120, 180, 360, 720];
    const idx = steps.indexOf(current);
    if (idx === -1) {
        // If unknown current, return nearest default
        return direction === 'up' ? 60 : 30;
    }
    if (direction === 'up') {
        return idx < steps.length - 1 ? steps[idx + 1] : steps[steps.length - 1];
    }
    return idx > 0 ? steps[idx - 1] : steps[0];
};
